import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plug, RefreshCw, ShieldCheck, ShieldX, AlertTriangle, ChevronRight } from 'lucide-react';
import { axios, auth, mbUrl, CONN_LABEL, fmtMyt, muted, primary, card, runCall, toast } from './mbApi';

const MODE_TEXT = { mock: 'Mock provider (bukan data sebenar)', live: 'Live', konfigurasi_salah: 'Konfigurasi mode SALAH — semak META_MCP_MODE (mock/live)' };

const ConnLabel = ({ settings }) => {
  const s = settings.connection_status;
  if (s === 'disambungkan' && !settings.last_check_at) return 'Belum disahkan (tiada semakan berjaya)';
  return CONN_LABEL[s] || s;
};

const ConnectionStatus = ({ settings, mcpInfo }) => (
  <div className="space-y-0.5">
    <p className={`text-xs ${muted}`}>
      Status: <span className={primary} data-testid="mb-connection-status"><ConnLabel settings={settings} /></span>
      {settings.is_demo ? <span className="text-amber-400"> · Channel demo — Meta/Telegram tidak dipanggil</span> : (
        <>{' · '}Mode: <span data-testid="mb-mcp-mode" className={mcpInfo.mode === 'konfigurasi_salah' ? 'text-red-400' : ''}>{MODE_TEXT[mcpInfo.mode]}</span>
          {' · '}Token: {mcpInfo.credentials_set ? 'ada (belum tentu sah — lihat semakan terakhir)' : 'belum set'}</>
      )}
    </p>
    {settings.last_error && <p className={`text-[11px] ${settings.connection_status === 'disambungkan' && !['separa', 'gagal'].includes(settings.last_sync_status) ? 'text-emerald-400' : 'text-amber-400'}`} data-testid="mb-connection-message">{settings.last_error}</p>}
    <p className={`text-[11px] ${muted} flex items-center gap-1`} data-testid="mb-tools-allowed">
      <ShieldCheck size={11} /> Tools dibenarkan (read-only): {(settings.tools_allowed || []).join(', ') || '— (run Check Connection)'}
      {settings.last_check_at ? ` · semakan terakhir ${fmtMyt(settings.last_check_at)}` : ''}
    </p>
    {(settings.tools_blocked || []).length > 0 && (
      <details className={`group text-[11px] ${muted}`} data-testid="mb-tools-blocked">
        <summary className="cursor-pointer flex items-center gap-1 list-none [&::-webkit-details-marker]:hidden" data-testid="mb-tools-blocked-toggle">
          <ChevronRight size={11} className="transition-transform duration-200 group-open:rotate-90" />
          <ShieldX size={11} className="text-red-400" /> {settings.tools_blocked.length} alat lain disekat — CRM baca sahaja, tak boleh ubah iklan · klik untuk senarai
        </summary>
        <p className="mt-1 pl-4 break-words">{settings.tools_blocked.join(', ')}</p>
      </details>
    )}
    <p className={`text-[11px] ${muted}`} data-testid="mb-last-sync">
      Last sync: {fmtMyt(settings.last_sync_at)}
      {settings.last_sync_range ? ` · data ${settings.last_sync_range}` : ''}
      {settings.last_sync_status === 'separa' && <span className="text-amber-400"> · data separa</span>}
      {settings.last_sync_status === 'gagal' && <span className="text-red-400"> · sync gagal</span>}
    </p>
  </div>
);

const SettingsForm = ({ ibId, settings, busy, setBusy, onSaved }) => {
  const [accounts, setAccounts] = useState((settings.ad_account_ids || []).join(', '));
  const [chatId, setChatId] = useState(settings.telegram_chat_id || '');
  const [lp, setLp] = useState(settings.lp_base_url || '');
  const [origin, setOrigin] = useState(settings.crm_public_origin || '');
  const save = (extra = {}, msg = 'Settings saved') => runCall(setBusy, async () => {
    const ids = accounts.split(',').map((a) => a.trim()).filter(Boolean);
    await axios.put(mbUrl(ibId, '/settings'), { ad_account_ids: ids, telegram_chat_id: chatId, lp_base_url: lp, crm_public_origin: origin, ...extra }, auth());
    onSaved();
  }, msg);
  const fields = [
    ['mb-accounts-input', 'Ad accounts dibenarkan (comma)', accounts, setAccounts, 'act_1234567890'],
    ['mb-telegram-chat-input', 'Telegram chat ID channel FIRA', chatId, setChatId, '-100…'],
    ['mb-lp-url-input', 'URL Landing Page (LP)', lp, setLp, 'https://fira.my/join'],
    ['mb-crm-origin-input', 'CRM public origin (kalau env CRM_PUBLIC_ORIGIN tak set)', origin, setOrigin, 'https://crm.domain.com'],
  ];
  return (
    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 items-end gap-3 border-t border-[hsl(var(--border))] pt-3">
      {fields.map(([id, label, value, setter, ph]) => (
        <div key={id} className="min-w-0">
          <label htmlFor={id} className={`text-xs ${muted}`}>{label}</label>
          <Input id={id} value={value} onChange={(e) => setter(e.target.value)} placeholder={ph} className="mt-1 h-10" data-testid={id} />
        </div>
      ))}
      <div className="md:col-span-2">
        <Button variant="outline" className="h-10" onClick={() => save()} disabled={busy} data-testid="mb-save-settings-btn">Simpan tetapan</Button>
      </div>
    </div>
  );
};

const EnableRow = ({ ibId, isAdmin, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const enable = () => runCall(setBusy, async () => {
    await axios.put(mbUrl(ibId, '/settings'), { enabled: true }, auth());
    onChanged();
  }, 'Marketing Hub dihidupkan');
  return (
    <div className="mt-3 border-t border-[hsl(var(--border))] pt-3">
      {isAdmin ? (
        <Button className="h-10 bg-gradient-to-r from-[#E63946] to-[#F77F00] text-white" disabled={busy} onClick={enable} data-testid="mb-toggle-btn">Hidupkan modul</Button>
      ) : <p className={`text-sm ${muted}`} data-testid="mb-enable-admin-only">Hanya admin boleh hidupkan / matikan modul.</p>}
    </div>
  );
};

const TelegramRow = ({ ibId, settings, telegramMode, canManage, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const check = () => runCall(setBusy, async () => {
    const r = await axios.post(mbUrl(ibId, '/telegram/check'), {}, auth());
    toast[r.data.status === 'disambungkan' || r.data.status === 'mock' ? 'success' : 'warning'](r.data.message);
    onChanged();
  });
  const register = () => runCall(setBusy, async () => {
    const r = await axios.post(mbUrl(ibId, '/telegram/register-webhook'), { base_url: process.env.REACT_APP_BACKEND_URL }, auth());
    toast[r.data.status === 'berjaya' ? 'success' : 'warning'](r.data.message || r.data.status);
  });
  const toggleAuto = () => runCall(setBusy, async () => {
    await axios.put(mbUrl(ibId, '/settings'), { auto_sync_enabled: !settings.auto_sync_enabled }, auth());
    onChanged();
  }, settings.auto_sync_enabled ? 'Auto sync off' : 'Auto sync on (setiap 1 jam)');
  return (
    <div className="mt-3 border-t border-[hsl(var(--border))] pt-3 flex flex-wrap items-center justify-between gap-2">
      <div className={`text-xs ${muted} space-y-0.5`}>
        <p>Bot Telegram Marketing Hub: <span className={primary} data-testid="mb-telegram-status">
          {telegramMode === 'mock' ? 'Mock (belum connect live)' : (CONN_LABEL[settings.telegram_status] || 'Belum semak')}</span>
          {settings.telegram_message && telegramMode !== 'mock' ? ` · ${settings.telegram_message}` : ''}
        </p>
        <p data-testid="mb-auto-sync-status">Auto sync setiap 1 jam: <span className={primary}>{settings.auto_sync_enabled ? 'ON' : 'OFF'}</span>
          {' '}— on-kan hanya lepas live diluluskan</p>
      </div>
      {canManage && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={check} data-testid="mb-telegram-check-btn">Check Bot</Button>
          {telegramMode === 'live' && <Button size="sm" variant="outline" disabled={busy} onClick={register} data-testid="mb-telegram-webhook-btn">Register Webhook</Button>}
          <Button size="sm" variant="outline" disabled={busy} onClick={toggleAuto} data-testid="mb-auto-sync-toggle">
            {settings.auto_sync_enabled ? 'Off Auto Sync' : 'On Auto Sync'}
          </Button>
        </div>
      )}
    </div>
  );
};

export const ConnectionCard = ({ ibId, settings, mcpInfo, telegramMode, canManage, isAdmin, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const check = () => runCall(setBusy, async () => {
    const r = await axios.post(mbUrl(ibId, '/connection/check'), {}, auth());
    toast[r.data.status === 'disambungkan' ? 'success' : 'warning'](`${CONN_LABEL[r.data.status] || r.data.status}: ${r.data.message}`);
    onChanged();
  });
  const sync = () => runCall(setBusy, async () => {
    const r = await axios.post(mbUrl(ibId, '/sync'), { days: 7 }, auth());
    toast.success(r.data.message || `Sync siap: ${r.data.synced} rows${r.data.status === 'separa' ? ' (separa)' : ''}`);
    onChanged();
  });
  return (
    <div className={card} data-testid="mb-connection-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={`text-sm font-semibold ${primary} flex items-center gap-2 mb-1`}><Plug size={15} /> Meta Ads MCP Connection</p>
          <ConnectionStatus settings={settings} mcpInfo={mcpInfo} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={check} disabled={busy || !canManage || !settings.enabled} data-testid="mb-check-btn">Check Connection</Button>
          <Button size="sm" variant="outline" onClick={sync} disabled={busy || !canManage || !settings.enabled} data-testid="mb-sync-btn">
            <RefreshCw size={13} className={`mr-1.5 ${busy ? 'animate-spin' : ''}`} /> Sync Now
          </Button>
        </div>
      </div>
      {mcpInfo.mode === 'mock' && (
        <p className="mt-2 text-[11px] text-amber-400 flex items-center gap-1" data-testid="mb-mock-banner">
          <AlertTriangle size={11} /> Mock mode — Meta & Telegram belum connect live. Nombor kat sini data contoh untuk testing je.
        </p>
      )}
      {settings.enabled && <TelegramRow ibId={ibId} settings={settings} telegramMode={telegramMode} canManage={canManage} onChanged={onChanged} />}
      {canManage && <SettingsForm ibId={ibId} settings={settings} busy={busy} setBusy={setBusy} onSaved={onChanged} />}
      {!settings.enabled && <EnableRow ibId={ibId} isAdmin={isAdmin} onChanged={onChanged} />}
    </div>
  );
};
